import { useEffect, useState, useCallback } from "react";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import Toast from "../components/Toast";

const API = process.env.REACT_APP_API_URL || "";
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const fmt = n => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });

export default function ServiceItems() {
  const { lang } = useLanguage();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [editTarget, setEditTarget] = useState(null); // null | {} | {_id,...}
  const [form,    setForm]    = useState({ name: "", price: "" });
  const [saving,  setSaving]  = useState(false);
  const [toasting, setToasting] = useState(null);

  const toast = (msg, type = "success") => {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 2600);
  };

  const fetchItems = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/service-items${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: authHeader() });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchItems(search), 280);
    return () => clearTimeout(t);
  }, [search, fetchItems]);

  const openNew  = () => { setForm({ name: "", price: "" }); setEditTarget({}); };
  const openEdit = it => { setForm({ name: it.name, price: String(it.price) }); setEditTarget(it); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast(t(lang, "siToastNameRequired"), "error"); return; }
    const p = Number(form.price);
    if (isNaN(p) || p < 0) { toast(t(lang, "siToastPriceInvalid"), "error"); return; }
    setSaving(true);
    try {
      const isNew  = !editTarget._id;
      const url    = isNew ? `${API}/api/service-items` : `${API}/api/service-items/${editTarget._id}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify({ name: form.name, price: p }) });
      if (!res.ok) throw new Error();
      toast(isNew ? t(lang, "siToastAdded") : t(lang, "siToastSaved"));
      setEditTarget(null);
      fetchItems(search);
    } catch { toast(t(lang, "siToastError"), "error"); }
    setSaving(false);
  };

  const handleDelete = async it => {
    if (!window.confirm(`${t(lang, "siConfirmDelete")} "${it.name}" ?`)) return;
    try {
      const res = await fetch(`${API}/api/service-items/${it._id}`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error();
      toast(t(lang, "siToastDeleted"));
      fetchItems(search);
    } catch { toast(t(lang, "siToastError"), "error"); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <NavbarAdmin />

      {/* ── Responsive ── บนจอแคบให้ปุ่มตกลงบรรทัดใหม่ ไม่อัดกับชื่อ/ราคา */}
      <style>{`
        @media (max-width: 520px) {
          .si-row { flex-wrap: wrap; }
          .si-actions { width: 100%; justify-content: flex-end; }
        }
      `}</style>


      <div style={{ maxWidth: 760, margin: "0 auto", padding: "clamp(16px, 4vw, 32px) clamp(12px, 3vw, 24px) 60px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
            {t(lang, "siTitle")}
          </div>
          <button onClick={openNew}
            style={{ padding: "9px 22px", borderRadius: 999, border: "none", background: "#1a1a1a",
                     color: "#fff", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {t(lang, "siAddBtn")}
          </button>
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#bbb" }}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t(lang, "siSearch")}
            style={{ width: "100%", padding: "10px 14px 10px 36px", borderRadius: 10, border: "1px solid #e0e0e0",
                     fontSize: 13.5, outline: "none", boxSizing: "border-box", background: "#fff", fontFamily: "inherit" }} />
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px 0", color: "#9ca3af", fontSize: 14,
                        background: "#fff", borderRadius: 14, border: "1px solid #e0e0e0" }}>{t(lang, "siLoading")}</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 0", color: "#9ca3af",
                        background: "#fff", borderRadius: 14, border: "1px solid #e0e0e0" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🏷️</div>
            <div style={{ fontSize: 14 }}>{search ? t(lang, "siNoSearch") : t(lang, "siNoItems")}</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map(it => (
              <div key={it._id} className="si-row"
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px",
                         background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "#1a1a1a" }}>{it.name}</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", whiteSpace: "nowrap" }}>
                  {fmt(it.price)} <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af" }}>{t(lang, "siThbUnit")}</span>
                </div>
                <div className="si-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => openEdit(it)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             background: "#1a1a1a", border: "none", borderRadius: 999, padding: "8px 18px",
                             cursor: "pointer", fontSize: 13, color: "#fff", fontWeight: 600, fontFamily: "inherit" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    {t(lang, "siEdit")}
                  </button>
                  <button onClick={() => handleDelete(it)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             background: "#fff", border: "1.5px solid #fca5a5", borderRadius: 999, padding: "8px 18px",
                             cursor: "pointer", fontSize: 13, color: "#dc2626", fontWeight: 600, fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6"/>
                    </svg>
                    {t(lang, "siDelete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit popup */}
      {editTarget !== null && (
        <div onClick={e => { if (e.target === e.currentTarget) setEditTarget(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000,
                   display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 18, width: "92vw", maxWidth: 400,
                        padding: "26px 28px", boxShadow: "0 12px 40px rgba(0,0,0,.2)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 18 }}>
              {editTarget._id ? t(lang, "siEditTitle") : t(lang, "siAddTitle")}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>{t(lang, "siNameLabel")}</label>
              <input autoFocus value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder={t(lang, "siNamePh")}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                         fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label style={{ fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>{t(lang, "siPriceLabel")}</label>
              <input type="number" min={0} value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                placeholder="0.00"
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                         fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setEditTarget(null)}
                style={{ flex: 1, padding: "11px", borderRadius: 999, border: "1px solid #d1d5db", background: "#fff",
                         color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{t(lang, "siCancelBtn")}</button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1.4, padding: "11px", borderRadius: 999, border: "none", background: "#1a1a1a",
                         color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                         opacity: saving ? 0.7 : 1 }}>
                {saving ? t(lang, "siSaving") : t(lang, "siSaveBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </div>
  );
}
